import type { VirtualizerHostElement } from "@lit-labs/virtualizer/virtualize.js";
import { virtualizerRef } from "@lit-labs/virtualizer/virtualize.js";
import type { LitElement } from "lit";

type Constructor<T> = new (...args: any[]) => T;

/**
 * Workaround for @lit-labs/virtualizer v2 not properly initializing when used
 * with the `virtualize` directive inside Lit v3 components. Reconnecting the
 * virtualizer in firstUpdated forces it to measure and render correctly.
 */
export const VirtualizerMixin = <T extends Constructor<LitElement>>(baseElement: T, selector = "[data-virtualized]") =>
    class extends baseElement {
        get _virtualizerHost(): VirtualizerHostElement | null {
            const el = this.renderRoot.querySelector(selector);
            if (el && virtualizerRef in el) {
                return el as VirtualizerHostElement;
            }
            return null;
        }

        get _virtualizer() {
            return this._virtualizerHost?.[virtualizerRef];
        }

        async firstUpdated(changedProperties: Map<PropertyKey, unknown>) {
            super.firstUpdated(changedProperties);
            try {
                this._virtualizer?.disconnected();
                this._virtualizer?.connected();
            } catch (e) {
                console.error("Failed to reconnect virtualizer:", e);
            }
        }
    };
